'use client';

import { apiClient } from './api-client';

// Types for error logging
interface ClientError {
  message: string;
  stack?: string;
  url?: string;
  userAgent?: string;
  timestamp: string;
  userId?: string;
  deviceInfo?: {
    platform?: string;
    version?: string;
    model?: string;
  };
  errorBoundary?: boolean;
  context?: Record<string, any>;
}

interface LogInfo {
  level: 'info' | 'warn' | 'error' | 'debug';
  message: string;
  context?: Record<string, any>;
  userId?: string;
  timestamp: string;
}

class ErrorLogger {
  private deviceInfo: ClientError['deviceInfo'];
  private userId?: string;
  private isEnabled: boolean = true;

  constructor() {
    this.deviceInfo = this.getDeviceInfoFromUserAgent();
  }

  private getDeviceInfoFromUserAgent() {
    if (typeof window === 'undefined') {
      return {
        platform: 'web',
        version: 'Unknown',
        model: 'Server',
      };
    }

    const userAgent = window.navigator.userAgent;
    let platform = 'web';
    let version = 'Unknown';
    let model = 'Desktop';

    // Detect platform
    if (userAgent.includes('iPhone')) {
      platform = 'iOS';
      model = 'iPhone';
      const match = userAgent.match(/OS (\d+_\d+)/);
      if (match) version = match[1].replace('_', '.');
    } else if (userAgent.includes('iPad')) {
      platform = 'iOS';
      model = 'iPad';
      const match = userAgent.match(/OS (\d+_\d+)/);
      if (match) version = match[1].replace('_', '.');
    } else if (userAgent.includes('Android')) {
      platform = 'Android';
      model = 'Mobile';
      const match = userAgent.match(/Android (\d+\.?\d*)/);
      if (match) version = match[1];
    } else if (userAgent.includes('Windows')) {
      platform = 'Windows';
      if (userAgent.includes('Windows NT 10.0')) version = '10';
      else if (userAgent.includes('Windows NT 6.3')) version = '8.1';
      else if (userAgent.includes('Windows NT 6.2')) version = '8';
      else if (userAgent.includes('Windows NT 6.1')) version = '7';
    } else if (userAgent.includes('Mac')) {
      platform = 'macOS';
      const match = userAgent.match(/Mac OS X (\d+[_\.]\d+)/);
      if (match) version = match[1].replace('_', '.');
    } else if (userAgent.includes('Linux')) {
      platform = 'Linux';
    }

    return { platform, version, model };
  }

  // Set user ID for context
  setUserId(userId: string) {
    this.userId = userId;
  }

  // Enable/disable logging
  setEnabled(enabled: boolean) {
    this.isEnabled = enabled;
  }

  // Get device and environment info
  private getDeviceInfo() {
    return {
      ...this.deviceInfo,
      userAgent:
        typeof window !== 'undefined' && window.navigator ? window.navigator.userAgent : 'Unknown',
    };
  }

  // Log an error to the server - DISABLED
  async logError(error: Error, context?: Record<string, any>, isErrorBoundary: boolean = false) {
    if (!this.isEnabled) return;

    try {
      const errorData: ClientError = {
        message: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
        userId: this.userId,
        deviceInfo: this.getDeviceInfo(),
        errorBoundary: isErrorBoundary,
        context,
      };

      await fetch(`${process.env.NEXT_PUBLIC_APP_URL}/error-logging/log`, {
        method: 'POST',
        body: JSON.stringify(errorData),
      });
    } catch (logError) {
      // Silently fail - don't want error logging to crash the app
    }
  }

  // Log info/warnings to the server - DISABLED
  async logInfo(level: LogInfo['level'], message: string, context?: Record<string, any>) {
    if (!this.isEnabled) return;

    // Prevent logging errors about logging failures to avoid infinite loops
    if (
      message.includes('Failed to log info to server') ||
      message.includes('Failed to send console log to server') ||
      (message.includes('Request failed with status code 404') &&
        message.includes('/error-logging/'))
    ) {
      return;
    }

    try {
      const logData: LogInfo = {
        level,
        message,
        context,
        userId: this.userId,
        timestamp: new Date().toISOString(),
      };

      await fetch(`${process.env.NEXT_PUBLIC_APP_URL}/error-logging/log`, {
        method: 'POST',
        body: JSON.stringify(logData),
      });
    } catch (logError) {
      // Silently fail - don't want error logging to crash the app
      // Don't use console.warn here to avoid interception loop
    }
  }

  // Convenience methods
  async logWarning(message: string, context?: Record<string, any>) {
    return this.logInfo('warn', message, context);
  }

  async logDebug(message: string, context?: Record<string, any>) {
    return this.logInfo('debug', message, context);
  }

  async logInfoMessage(message: string, context?: Record<string, any>) {
    return this.logInfo('info', message, context);
  }
}

// Create a singleton instance
export const errorLogger = new ErrorLogger();

if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    const error = new Error(event.reason?.message || 'Unhandled Promise Rejection');
    error.stack = event.reason?.stack;

    errorLogger.logError(error, {
      type: 'unhandledRejection',
      reason: event.reason,
    });
  });
}
